import React, { useState, useEffect } from 'react';
import { 
  message, 
  Spin, 
  Button, 
  Space, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Divider 
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import FinanceRecordList from './FinanceRecordList';
import AddFinanceRecordModal from './AddFinanceRecordModal';
import LeadFinanceConfigModal from './LeadFinanceConfigModal';
import LeadMilestoneProgress from './LeadMilestoneProgress';
import {
  getFinanceSummary,
  getFinanceRecords,
  deleteFinanceRecord,
  EntityType,
} from '../../services/finance';
import {
  getLeadFinanceConfig,
} from '../../services/financeConfig';
import type { FinanceSummary, FinanceRecord } from '../../types/finance';
import type { LeadFinanceFullConfig } from '../../types/financeConfig';
import { PaymentMode } from '../../types/financeConfig';

const { Text } = Typography;

interface FinanceTabProps {
  leadId: number;
  lead: any;
  canEdit: boolean;
}

/**
 * 财务情况标签页组件
 * 集成财务概览、财务记录列表和新增财务记录功能
 */
const FinanceTab: React.FC<FinanceTabProps> = ({ leadId, lead, canEdit }) => {
  // State management
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // 财务配置相关状态
  const [financeConfig, setFinanceConfig] = useState<LeadFinanceFullConfig | null>(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);

  /**
   * Load finance summary
   */
  const loadFinanceSummary = async () => {
    try {
      const data = await getFinanceSummary(EntityType.LEAD, leadId);
      // Convert string amounts to numbers if needed
      const normalizedSummary: FinanceSummary = {
        contract_amount: typeof data.contract_amount === 'string' 
          ? parseFloat(data.contract_amount) 
          : data.contract_amount,
        total_received: typeof data.total_received === 'string' 
          ? parseFloat(data.total_received) 
          : data.total_received,
        total_refund: typeof data.total_refund === 'string' 
          ? parseFloat(data.total_refund) 
          : data.total_refund,
        total_receivable: typeof data.total_receivable === 'string' 
          ? parseFloat(data.total_receivable) 
          : data.total_receivable,
        net_received: typeof data.net_received === 'string' 
          ? parseFloat(data.net_received) 
          : data.net_received,
      };
      setSummary(normalizedSummary);
    } catch (error: any) {
      console.error('Failed to load finance summary:', error);
      // Don't show error message for summary as it might not exist yet
    }
  };

  /**
   * 加载财务配置
   */
  const loadFinanceConfig = async () => {
    try {
      setLoadingConfig(true);
      const data = await getLeadFinanceConfig(leadId);
      setFinanceConfig(data);
    } catch (error: any) {
      // 如果没有配置，不显示错误
      console.log('No finance config or error loading:', error);
      setFinanceConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  };

  /**
   * Load finance records
   */
  const loadFinanceRecords = async () => {
    try {
      const data = await getFinanceRecords(EntityType.LEAD, leadId);
      // Convert string amounts to numbers if needed and ensure required fields
      const normalizedRecords = (data.items || []).map(record => ({
        ...record,
        amount: typeof record.amount === 'string' 
          ? parseFloat(record.amount) 
          : record.amount,
        updated_at: record.updated_at || record.created_at, // Ensure updated_at is always present
      }));
      setRecords(normalizedRecords);
    } catch (error: any) {
      console.error('Failed to load finance records:', error);
      message.error('加载财务记录失败');
    }
  };

  /**
   * Load all finance data
   */
  const loadFinanceData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadFinanceSummary(), 
        loadFinanceRecords(),
        loadFinanceConfig(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh finance data
   */
  const refreshFinanceData = () => {
    setRefreshKey((prev) => prev + 1);
  };

  /**
   * 获取付费模式显示文本
   */
  const getPaymentModeText = (mode: PaymentMode) => {
    const modeMap = {
      [PaymentMode.FIXED]: '固定收费',
      [PaymentMode.MILESTONE]: '节点付费',
      [PaymentMode.RISK_AGENCY]: '风险代理',
      [PaymentMode.HOURLY]: '按小时计费',
    };
    return modeMap[mode] || mode;
  };

  /**
   * Handle delete finance record
   */
  const handleDeleteRecord = async (recordId: number) => {
    try {
      await deleteFinanceRecord(EntityType.LEAD, leadId, recordId);
      message.success('财务记录已删除');
      // Refresh both summary and records after deletion
      refreshFinanceData();
    } catch (error: any) {
      console.error('Failed to delete finance record:', error);
      message.error('删除失败，请重试');
    }
  };

  /**
   * Handle add finance record success
   */
  const handleAddSuccess = () => {
    // Refresh both summary and records after adding new record
    refreshFinanceData();
  };

  /**
   * Handle open add modal
   */
  const handleOpenAddModal = () => {
    setModalVisible(true);
  };

  /**
   * Handle close add modal
   */
  const handleCloseAddModal = () => {
    setModalVisible(false);
  };

  /**
   * 打开财务配置弹窗
   */
  const handleOpenConfigModal = () => {
    setConfigModalVisible(true);
  };

  /**
   * 关闭财务配置弹窗
   */
  const handleCloseConfigModal = () => {
    setConfigModalVisible(false);
  };

  /**
   * 财务配置保存成功
   */
  const handleConfigSuccess = () => {
    loadFinanceConfig();
    loadFinanceSummary(); // 刷新财务汇总，因为合同金额可能变化
    handleCloseConfigModal();
  };

  // Load finance data on mount and when refreshKey changes
  useEffect(() => {
    if (leadId) {
      loadFinanceData();
    }
  }, [leadId, refreshKey]);

  // Show loading state
  if (loading && records.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="加载财务数据中..." />
      </div>
    );
  }

  return (
    <div className="lead-finance-tab" style={{ padding: '16px' }}>
      {/* 财务配置和汇总整合卡片 */}
      <Card
        title="财务概览"
        style={{ marginBottom: 24 }}
        extra={
          canEdit && (
            <Space>
              {financeConfig && financeConfig.id > 0 ? (
                <Button
                  type="link"
                  icon={<SettingOutlined />}
                  onClick={handleOpenConfigModal}
                >
                  编辑配置
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<SettingOutlined />}
                  onClick={handleOpenConfigModal}
                >
                  配置财务模式
                </Button>
              )}
            </Space>
          )
        }
        loading={loadingConfig || loading}
      >
        {financeConfig ? (
          <>
            {/* 财务配置信息 */}
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* 基本信息 */}
                <Space split={<Divider type="vertical" />} size="large" wrap>
                  <Text>
                    付费模式：<Text strong>{getPaymentModeText(financeConfig.payment_mode)}</Text>
                  </Text>
                  <Text>
                    合同金额：<Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                      ¥{Number(financeConfig.contract_amount).toLocaleString('zh-CN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Text>
                  {financeConfig.payment_mode === PaymentMode.MILESTONE && financeConfig.milestone_progress && (
                    <Text>
                      节点进度：<Text strong style={{ color: '#52c41a' }}>
                        {financeConfig.milestone_progress.paid_milestones}/{financeConfig.milestone_progress.total_milestones} 已完成
                      </Text>
                    </Text>
                  )}
                </Space>

                {/* 风险代理详细信息 */}
                {financeConfig.payment_mode === PaymentMode.RISK_AGENCY && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    borderRadius: 4,
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ color: '#52c41a' }}>风险代理详情</Text>
                      <Space split={<Divider type="vertical" />} size="large" wrap>
                        {financeConfig.risk_base_fee && (
                          <Text>
                            基础费用：<Text strong>¥{Number(financeConfig.risk_base_fee).toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}</Text>
                          </Text>
                        )}
                        {financeConfig.risk_success_rate && (
                          <Text>
                            成功比例：<Text strong>{(Number(financeConfig.risk_success_rate) * 100).toFixed(2)}%</Text>
                          </Text>
                        )}
                        {financeConfig.risk_target_amount && (
                          <Text>
                            预期标的：<Text strong>¥{Number(financeConfig.risk_target_amount).toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}</Text>
                          </Text>
                        )}
                      </Space>
                      {(financeConfig.risk_min_fee || financeConfig.risk_max_fee) && (
                        <Space split={<Divider type="vertical" />} size="large">
                          {financeConfig.risk_min_fee && (
                            <Text>
                              最低收费：<Text strong>¥{Number(financeConfig.risk_min_fee).toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}</Text>
                            </Text>
                          )}
                          {financeConfig.risk_max_fee && (
                            <Text>
                              最高收费：<Text strong>¥{Number(financeConfig.risk_max_fee).toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}</Text>
                            </Text>
                          )}
                        </Space>
                      )}
                      {financeConfig.risk_payment_condition && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          付费条件：{financeConfig.risk_payment_condition}
                        </Text>
                      )}
                    </Space>
                  </div>
                )}

                {/* 按小时计费详细信息 */}
                {financeConfig.payment_mode === PaymentMode.HOURLY && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#e6f7ff', 
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ color: '#1890ff' }}>按小时计费详情</Text>
                      <Space split={<Divider type="vertical" />} size="large" wrap>
                        {financeConfig.hourly_rate && (
                          <Text>
                            小时费率：<Text strong>¥{Number(financeConfig.hourly_rate).toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}/小时</Text>
                          </Text>
                        )}
                        {financeConfig.estimated_hours && (
                          <Text>
                            预估工时：<Text strong>{financeConfig.estimated_hours} 小时</Text>
                          </Text>
                        )}
                        {financeConfig.hourly_rate && financeConfig.estimated_hours && (
                          <Text>
                            预估总费用：<Text strong style={{ color: '#1890ff' }}>
                              ¥{(Number(financeConfig.hourly_rate) * financeConfig.estimated_hours).toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Text>
                          </Text>
                        )}
                      </Space>
                    </Space>
                  </div>
                )}

                {/* 备注 */}
                {financeConfig.notes && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    备注：{financeConfig.notes}
                  </Text>
                )}
              </Space>
            </div>

            {/* 财务统计 */}
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="已收金额"
                  value={summary?.total_received || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="待收金额"
                  value={summary?.total_receivable || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="退款金额"
                  value={summary?.total_refund || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="净收款"
                  value={summary?.net_received || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ 
                    color: summary && (typeof summary.net_received === 'number' ? summary.net_received : parseFloat(String(summary.net_received))) >= 0 ? '#52c41a' : '#ff4d4f' 
                  }}
                />
              </Col>
            </Row>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
              尚未配置财务模式，建议先配置后再添加财务记录
            </Text>
            {canEdit && (
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={handleOpenConfigModal}
              >
                立即配置
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* 节点付费进度展示 */}
      {financeConfig?.payment_mode === PaymentMode.MILESTONE && financeConfig.milestone_progress && (
        <LeadMilestoneProgress
          leadId={leadId}
          milestones={financeConfig.milestones || []}
          progress={financeConfig.milestone_progress}
          contractAmount={financeConfig.contract_amount}
          canEdit={canEdit}
          onRefresh={loadFinanceConfig}
        />
      )}

      {/* 如果是节点付费模式但还没有节点，显示空的进度卡片 */}
      {financeConfig?.payment_mode === PaymentMode.MILESTONE && 
       (!financeConfig.milestones || financeConfig.milestones.length === 0) && (
        <LeadMilestoneProgress
          leadId={leadId}
          milestones={[]}
          progress={{
            total_milestones: 0,
            pending_milestones: 0,
            paid_milestones: 0,
            overdue_milestones: 0,
            total_amount: 0,
            paid_amount: 0,
            remaining_amount: 0,
            payment_progress: 0,
          }}
          contractAmount={financeConfig.contract_amount}
          canEdit={canEdit}
          onRefresh={loadFinanceConfig}
        />
      )}

      {/* Finance Record List */}
      <FinanceRecordList
        records={records}
        leadId={leadId}
        canEdit={canEdit}
        loading={loading}
        onAdd={handleOpenAddModal}
        onDelete={handleDeleteRecord}
        onRefresh={refreshFinanceData}
      />

      {/* Add Finance Record Modal */}
      <AddFinanceRecordModal
        visible={modalVisible}
        leadId={leadId}
        onCancel={handleCloseAddModal}
        onSuccess={handleAddSuccess}
      />

      {/* 财务配置弹窗 */}
      <LeadFinanceConfigModal
        visible={configModalVisible}
        leadId={leadId}
        config={financeConfig}
        onCancel={handleCloseConfigModal}
        onSuccess={handleConfigSuccess}
      />
    </div>
  );
};

export default FinanceTab;
